import { useCallback, useRef } from "react";
import { makeDefaultDeps } from "../commands/deps.js";
import { submitForecast, doForecast as cmdDoForecast } from "../commands/submitForecast.js";

/**
 * React binding for the forecast-submit command (EPIC #157, phase P5.1). The
 * operation itself — `submitForecast` / `doForecast` / `postSubmit` — moved verbatim
 * into `commands/submitForecast.ts`; this hook is now just the ~binding that packs the
 * App's React handles into the command's deps bag and exposes the two ops memoized.
 *
 * The store handles are gone from the deps: the command reads/writes the external
 * run-store directly (via `makeDefaultDeps().store`), so the hook no longer threads
 * `dispatchRun`/`runStateRef` (retires the ratchet's `hooksTakingRunArgs`/§7 #1 for
 * this op). The exec table is the `store/execTable` module singleton the command imports.
 *
 * The two `useCallback` dep arrays are the ORIGINAL reactive sets (plus the members that
 * used to reach `doForecast` transitively through the now-inlined `postSubmit`), so the
 * returned identities change on exactly the same renders as before — consumers that list
 * `submit`/`doForecast` in their own dep arrays (dispatchIntent, useChatMessages, the tour
 * controller) re-memoize unchanged. `mkDeps()` reads the memo-time closure, matching the
 * values the original function bodies closed over.
 */
export function useForecastSubmit(deps) {
  const {
    // state
    input,
    loading,
    forecastEngine,
    compareMode,
    compareEngine,
    triggerLlm,
    compareTriggerLlm,
    debugMode,
    forecastLiveProgressEnabled,
    series,
    multiwayEnabled,
    autoQuestionTypeEnabled,
    preprocessVariantId,
    // setters
    setInput,
    setLoading,
    setForecastingPhase,
    setPendingPreprocess,
    setActiveTrace,
    setLastObs,
    setForecastEngine,
    // refs (stable)
    forecastAbortRef,
    submitAbortRef,
    slideCache,
    pushSayerMsgRef,
    preprocessConfirmedRef,
    contractNotePendingRef,
    lastTourPendingIdRef,
    postTourNudgeTimerRef,
    postTourTipRef,
    postTourTipRunRef,
    pendingEngineRevertRef,
    forecastErrorStreakRef,
    // callbacks
    autoZoom,
    blinkContract,
    startForecastAnim,
    fetchLiveSeries,
    clearSeriesSelection,
    guardForTos,
    clearDraft,
    reattachToForecast,
    // tour-global getters (threaded, not copied — read live at call time)
    getTourFixture,
    getTourBranchIncrease,
  } = deps;
  const forecastLiveProgressEnabledRef = useRef(forecastLiveProgressEnabled);
  forecastLiveProgressEnabledRef.current = forecastLiveProgressEnabled;

  // Assemble the command deps from the App's React handles. Built fresh each render;
  // captured by the memoized callbacks at their memo-time render, so the values match
  // the original hook's closure exactly.
  const mkDeps = () => ({
    ...makeDefaultDeps(),
    ui: {
      input,
      loading,
      forecastEngine,
      compareMode,
      compareEngine,
      triggerLlm,
      compareTriggerLlm,
      debugMode,
      forecastLiveProgressEnabled,
      series,
      multiwayEnabled,
      autoQuestionTypeEnabled,
      preprocessVariantId,
      setInput,
      setLoading,
      setForecastingPhase,
      setPendingPreprocess,
      setActiveTrace,
      setLastObs,
      setForecastEngine,
    },
    refs: {
      forecastAbortRef,
      submitAbortRef,
      slideCache,
      pushSayerMsgRef,
      preprocessConfirmedRef,
      contractNotePendingRef,
      lastTourPendingIdRef,
      postTourNudgeTimerRef,
      postTourTipRef,
      postTourTipRunRef,
      pendingEngineRevertRef,
      forecastErrorStreakRef,
      forecastLiveProgressEnabledRef,
    },
    callbacks: {
      autoZoom,
      blinkContract,
      startForecastAnim,
      fetchLiveSeries,
      clearSeriesSelection,
      guardForTos,
      clearDraft,
      // Stable ([]-deps) identity, so it needs no slot in the dep arrays below.
      reattachRunning: reattachToForecast,
    },
    tour: { getTourFixture, getTourBranchIncrease },
  });

  // doForecast(q, engine, preprocessed, _baseMsgs, pendingId, msgId, routing) — legacy arg order
  // preserved so App.jsx callers are untouched; `msgId` and `routing` are appended (optional). Dep array =
  // doForecast's original reactive set PLUS postSubmit's (autoZoom/blinkContract/
  // startForecastAnim/series/fetchLiveSeries) — those used to fold in through the postSubmit
  // identity, now inlined into the command, so they must appear here to preserve the
  // re-memoization cadence.
  const doForecast = useCallback(
    (q, engine, preprocessed, _baseMsgs, pendingId, msgId = null, routing = null) =>
      cmdDoForecast(mkDeps(), { q, engine, preprocessed, pendingId, msgId, routing }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      compareMode,
      compareEngine,
      triggerLlm,
      compareTriggerLlm,
      debugMode,
      forecastLiveProgressEnabled,
      guardForTos,
      setForecastEngine,
      autoZoom,
      blinkContract,
      startForecastAnim,
      series,
      fetchLiveSeries,
    ],
  );

  // submit(overrideText, overrideEngine, force) — legacy arg order preserved. Dep array
  // is submit's original reactive set PLUS `preprocessVariantId`: the command body reads it
  // at submit time, so without it a variant chosen after mount would never reach a `submit`
  // captured on an earlier render. `doForecast` (whose identity now tracks the members
  // above) keeps submit re-memoizing on the same renders it did before.
  const submit = useCallback(
    (overrideText, overrideEngine, force = false, turn = null) => {
      // `turn` is the chat turn the CALLER opened (lib/chatTurn.js), passed explicitly: a
      // slow earlier message handing off here must close its own turn, never whichever one
      // is current by then. submitForecast closes it at the moment the user sees the
      // result; this only catches exits nobody labelled, so a turn can never stay open.
      // First close wins, and the derived chain handles its own rejection (the caller
      // still gets `run` unchanged).
      const run = submitForecast(mkDeps(), { overrideText, overrideEngine, force, turn });
      if (turn)
        Promise.resolve(run).then(
          () => turn.finish("unmarked"),
          () => turn.finish("error"),
        );
      return run;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input, loading, forecastEngine, doForecast, autoZoom, guardForTos, preprocessVariantId],
  );

  return { submit, doForecast };
}
